import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { SafeDialog } from "components/Dialog";
import { ImportChangeSummary } from "generated-graphql/graphql";

export interface ConfirmImportDialogProps {
  open: boolean;
  preview: Pick<
    ImportChangeSummary,
    "entityLabel" | "recordsAffected" | "added" | "edited" | "deleted"
  > | null;
  /** True while the preview counts are still being computed (dialog opens immediately). */
  previewLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

export function ConfirmImportDialog({
  open,
  preview,
  previewLoading,
  onConfirm,
  onCancel,
  loading,
}: ConfirmImportDialogProps) {
  const showCounts = !previewLoading && preview !== null;
  const fieldWord = (n: number) => (n === 1 ? "field" : "fields");

  return (
    <SafeDialog open={open}>
      <DialogTitle>Confirm import</DialogTitle>
      <DialogContent>
        {showCounts ? (
          <Stack spacing={2}>
            <DialogContentText>Review the changes below and confirm to proceed.</DialogContentText>
            <Stack spacing={1}>
              <Typography variant="body1">
                {(preview.added + preview.edited).toLocaleString()}{" "}
                {fieldWord(preview.added + preview.edited)} added or edited
              </Typography>
              <Typography variant="body1">
                {preview.deleted.toLocaleString()} {fieldWord(preview.deleted)} deleted
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {preview.recordsAffected.toLocaleString()} {preview.entityLabel}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress />
            <DialogContentText>Analyzing changes…</DialogContentText>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading || previewLoading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" loading={loading} disabled={!showCounts}>
          Confirm
        </Button>
      </DialogActions>
    </SafeDialog>
  );
}
